import { z } from 'zod'

/**
 * Which vaulted credential one app uses, in one lane (credential-home plan U1,
 * R4 / KTD1 / KTD2).
 *
 * The vocabularies below are the closed unions the database deliberately does
 * NOT enforce. `lane`, the coverage state and the liveness state are string
 * columns rather than database enums for the reason
 * {@link ./publishing-account.ts} gives one model over: every new enum value
 * costs a hand-performed PlanetScale promotion, and each of these is expected to
 * grow. THIS FILE is where the closed set actually lives, so a value is
 * validated here rather than by MySQL.
 */

/**
 * The credential lanes an app can be pointed at.
 *
 * FOUR OF THESE ARE `PROVISIONING_PROVIDERS`' VALUES and are spelled
 * identically on purpose — the vault files a credential under the provider
 * string, so a lane that differed by so much as a hyphen would name a row that
 * cannot be found. `onesignal` is the fifth, and has no provider member until
 * U7 moves that key off the `MobileApp` row and into the vault.
 *
 * `fcm-development` and `fcm-preview` (SCH-2102 KTD7) are the FIXTURE
 * environments' Firebase-push lanes. They are NOT providers: the vault row each
 * names is still provider `fcm`, filed under that environment's own Firebase
 * project. `fcm` itself is always the production project's key. See
 * {@link FCM_LANE_BY_ENVIRONMENT}.
 *
 * NOT ONE AXIS. Three lanes are owned by a publishing account, the Firebase-push
 * lanes by a Firebase project, and OneSignal by the app itself — which is why the
 * picker (U6) scopes by lane first and by that lane's owner second, and why no
 * single account column can filter them all.
 */
export const CREDENTIAL_LANES = [
  'apple-asc',
  'apns',
  'google-play',
  'fcm',
  'fcm-development',
  'fcm-preview',
  'onesignal'
] as const

export const CredentialLaneSchema = z.enum(CREDENTIAL_LANES)
export type CredentialLane = z.infer<typeof CredentialLaneSchema>

/**
 * Which pointer lane holds the FCM key for one Firebase environment (KTD7).
 *
 * Keyed by the same `production | development | preview` vocabulary as
 * `MobileAppFirebaseProject.environment`, so a resolver asks for an
 * environment and never spells a lane by hand.
 */
export const FCM_LANE_BY_ENVIRONMENT = {
  production: 'fcm',
  development: 'fcm-development',
  preview: 'fcm-preview'
} as const satisfies Record<'production' | 'development' | 'preview', CredentialLane>

export type FcmCredentialLane = (typeof FCM_LANE_BY_ENVIRONMENT)[keyof typeof FCM_LANE_BY_ENVIRONMENT]

/**
 * The outcome of the LAST attempt to observe a credential fact, whether that
 * attempt succeeded or not (KTD7).
 *
 * TWO VALUES, AND THE SECOND IS THE POINT — the same shape, and the same rule,
 * as {@link ObservedStandingCheckStatusSchema} one grain up. `stale_check` is
 * recorded BESIDE the last real reading rather than replacing it, because a
 * check that did not run must never clear a finding: silence is not recovery,
 * and a sweep that could not reach a vendor must not turn a known-covered app
 * into an unknown one.
 */
export const CredentialCheckStatusSchema = z.enum(['observed', 'stale_check'])
export type CredentialCheckStatus = z.infer<typeof CredentialCheckStatusSchema>

/**
 * Whether the credential an app points at actually covers that app (R11).
 *
 * `not_covered` IS ITS OWN STATE, distinct from having no credential at all
 * (AE6). An app whose pointer names a key filed under an account that does not
 * list that app is not missing a credential — it has one, and it is the wrong
 * one. Collapsing the two tells an operator to upload a key they already have.
 *
 * `never_checked` is the default and is never guessed away: coverage is only
 * answerable by asking the vendor, which R12 forbids a reader from doing, so an
 * unchecked pointer reports as unchecked rather than as covered.
 */
export const CredentialCoverageStateSchema = z.enum(['never_checked', 'covered', 'not_covered'])
export type CredentialCoverageState = z.infer<typeof CredentialCoverageStateSchema>

/**
 * Whether a vaulted credential could still be READ (R10, KTD7).
 *
 * THREE STATES, NEVER A BOOLEAN. A key that sits in the vault and no longer
 * decrypts — a rotated KMS key, the wrong crypto mode — is not an empty slot,
 * and reporting it as one sends an operator to re-upload a credential that in
 * the App Store Connect case cannot be downloaded twice. `integrationExists`
 * cannot tell the two apart and R12 forbids a presence reader from decrypting to
 * find out, so this is recorded out of band and read as stored state.
 */
export const CredentialLivenessStateSchema = z.enum(['never_checked', 'retrievable', 'unreadable'])
export type CredentialLivenessState = z.infer<typeof CredentialLivenessStateSchema>

/**
 * One app's pointer in one lane, as a client sees it.
 *
 * `integrationPublicId` is the vault row's OWN public id (KTD2), never its
 * provider/label pair: the account-lane label embeds the vendor key identifier
 * and R22 rewrites a placeholder account identifier in place, so a label-based
 * pointer would break exactly when an account graduates from placeholder to
 * real.
 *
 * `assignedBy` NULL means the pipeline chose this credential — the U5 backfill,
 * or the automatic selection R8 makes when a lane's owner holds exactly one —
 * and a value means a human picked it. Without that split there is no way to
 * tell a considered choice from a default that happened to land.
 *
 * THE COVERAGE FIELDS DESCRIBE TWO DIFFERENT QUESTIONS and must be read as a
 * pair: `coverageCheckStatus` / `coverageLastCheckedAt` say whether anyone has
 * been able to ask lately, while `coverageState` / `coverageObservedAt` say what
 * the last successful answer was. A failed check moves the first pair only.
 */
export const MobileAppCredentialPointerSchema = z.object({
  publicId: z.string(),
  /** `MobileApp.publicId` — the app this pointer belongs to. */
  mobileAppPublicId: z.string(),
  lane: CredentialLaneSchema,
  /** `Integration.publicId` — the vault row this app uses in this lane. */
  integrationPublicId: z.string(),
  /** ISO-8601. */
  assignedAt: z.string(),
  /** Who chose it, or null when the pipeline did. */
  assignedBy: z.string().nullable(),
  coverageState: CredentialCoverageStateSchema,
  coverageCheckStatus: CredentialCheckStatusSchema,
  coverageCheckError: z.string().nullable(),
  /** Every attempt. Null until anything has checked this pointer. */
  coverageLastCheckedAt: z.string().nullable(),
  /** Only a successful read. Null when coverage has never been established. */
  coverageObservedAt: z.string().nullable()
})
export type MobileAppCredentialPointer = z.infer<typeof MobileAppCredentialPointerSchema>

/**
 * One credential's current liveness reading, as a client sees it.
 *
 * Keyed by the vault row rather than by an app: many apps can name one
 * credential (eight share Apple team `53X6FN77Q8` today), so asking "can this
 * key still be read" once per credential rather than once per app is both the
 * cheaper question and the only one with a single answer.
 */
export const ProvisioningCredentialLivenessSchema = z.object({
  /** `Integration.publicId` — the vault row this reading describes. */
  integrationPublicId: z.string(),
  livenessState: CredentialLivenessStateSchema,
  checkStatus: CredentialCheckStatusSchema,
  checkError: z.string().nullable(),
  /** Every attempt, successful or not. */
  lastCheckedAt: z.string(),
  /** Only a successful read. Null when this credential has never been read. */
  observedAt: z.string().nullable()
})
export type ProvisioningCredentialLiveness = z.infer<typeof ProvisioningCredentialLivenessSchema>
